// src/repositories/types.ts
//
// Repository Pattern — TryQuiPus
//
// Estas interfaces desacoplan la UI de la capa de persistencia.
// Implementación actual: Dexie.js (ver CompanyRepository.ts, etc.)
// Futura: SupabaseXxxRepository implementando las mismas interfaces.

import type {
  DBCompany,
  DBTransaction,
  DBJournalEntry,
  DBSunatRecord,
  DBEntity,
} from '../types/db';
import type { Company } from '../types';
import type { AgentProposal, NuevaPropuesta, EstadoPropuesta, AgentePropuesta, JsonObject } from '../agents/proposals/types';

// Re-export DB types so consumers import from one place
export type { DBCompany, DBTransaction, DBJournalEntry, DBSunatRecord, DBEntity };

// ─── Company ────────────────────────────────────────────────────────────────

export interface ICompanyRepository {
  /** All companies for a given firm. */
  getAll(firmId: string): Promise<DBCompany[]>;

  /** Find a company by RUC within a firm. Returns undefined if not found. */
  getByRuc(firmId: string, ruc: string): Promise<DBCompany | undefined>;

  /** Register a new company. Throws if RUC already exists in the firm. */
  create(company: Company): Promise<void>;

  /** Update editable fields of a company. */
  update(firmId: string, companyId: string, fields: { razonSocial?: string; regimen?: string; domicilioFiscal?: string }): Promise<void>;

  /** Delete a company by companyId. Does NOT cascade-delete related records. */
  delete(firmId: string, companyId: string): Promise<void>;
}

// ─── Transaction ─────────────────────────────────────────────────────────────

export interface ITransactionRepository {
  /** Transactions for a company filtered by period ("YYYY-MM"). */
  getByPeriod(
    firmId: string,
    companyId: string,
    periodo: string,
  ): Promise<DBTransaction[]>;

  /** Transactions YTD (Jan 1 → periodo) for NIC 7 Flujo de Efectivo. */
  getByPeriodYTD(
    firmId: string,
    companyId: string,
    periodo: string,
  ): Promise<DBTransaction[]>;

  /** All transactions for a company, optionally filtered by period. */
  getByCompany(firmId: string, companyId: string, periodo?: string): Promise<DBTransaction[]>;

  /**
   * Row count for a company+period (used for KPI strip in AccountantConsole).
   * NOTE: Dexie implementation fetches all rows to count. Future server-side
   * implementations should use a native COUNT(*) query instead.
   * @deferred — AccountantConsole currently measures .length on fetched arrays.
   *             Switch to countByPeriod() when optimizing for large datasets or Supabase.
   */
  countByPeriod(
    firmId: string,
    companyId: string,
    periodo: string,
  ): Promise<number>;
}

// ─── Journal Entry ────────────────────────────────────────────────────────────

export interface IJournalEntryRepository {
  /** Journal entries for a company+period ("YYYY-MM"). */
  getByPeriod(
    firmId: string,
    companyId: string,
    periodo: string,
  ): Promise<DBJournalEntry[]>;

  /** Journal entries YTD (Jan 1 → periodo) for NIC 1 Estado de Resultados. */
  getByPeriodYTD(
    firmId: string,
    companyId: string,
    periodo: string,
  ): Promise<DBJournalEntry[]>;

  /**
   * Journal entries acumulados desde el inicio de la empresa hasta `periodo`
   * (sin límite inferior — a diferencia de getByPeriodYTD, que resetea cada
   * enero). Para Balance General, que es acumulativo por naturaleza (Activo/
   * Pasivo/Patrimonio), no un flujo del período. Mismo criterio que la query
   * inline de Balance.tsx.
   */
  getCumulative(
    firmId: string,
    companyId: string,
    periodo: string,
  ): Promise<DBJournalEntry[]>;

  /**
   * Entry count for a company+period.
   * NOTE: Dexie implementation fetches all rows to count. Future server-side
   * implementations should use a native COUNT(*) query instead.
   * @deferred — AccountantConsole currently measures .length on fetched arrays.
   *             Switch to countByPeriod() when optimizing for large datasets or Supabase.
   */
  countByPeriod(
    firmId: string,
    companyId: string,
    periodo: string,
  ): Promise<number>;

  /**
   * Count of journal entries with posted=false for the period.
   * Used as a pre-flight check before PLE export to warn the user that
   * unposted entries will NOT appear in the exported file.
   */
  countUnpostedByPeriod(
    firmId: string,
    companyId: string,
    periodo: string,
  ): Promise<number>;
}

// ─── SUNAT Record ─────────────────────────────────────────────────────────────

export interface ISunatRecordRepository {
  /**
   * Records by type and optional period.
   * If `periodo` is omitted, returns all periods for the type.
   *
   * ⚠️ PERIOD FORMAT: DBSunatRecord stores periodo as "YYYYMM" (6 chars, e.g. "202501").
   * AccountantConsole uses "YYYY-MM" (ISO, 7 chars). Pass the correct format for the
   * underlying store — the Dexie implementation does strict string equality.
   */
  getByType(
    firmId: string,
    companyId: string,
    tipo: 'compra' | 'venta',
    periodo?: string,
  ): Promise<DBSunatRecord[]>;

  /** Most recent record for a given type (used for "last submission" badge). */
  getLatest(
    firmId: string,
    companyId: string,
    tipo: 'compra' | 'venta',
  ): Promise<DBSunatRecord | undefined>;
}

// ─── Invoice Payment ─────────────────────────────────────────────────────────

/**
 * Fila enriquecida de la vista SQL `invoice_payment_status` + join con
 * `transactions`. Contiene todo lo necesario para que un agente reporte
 * facturas vencidas sin requerir queries adicionales.
 *
 * `monto` es **firmado** (negativo = CxP, positivo = CxC). Las columnas
 * `saldoPendiente`, `montoNeto`, `montoBruto`, `montoPagado` son **absolutas**
 * (siempre >= 0) tal como las expone la vista.
 */
export interface OverdueInvoiceRow {
  invoiceDbId:      number;
  firmId:           string;
  companyId:        string;

  // Estado de pago (vista)
  saldoPendiente:   number;
  montoBruto:       number;
  montoNeto:        number;
  montoPagado:      number;
  paymentStatus:    'pendiente' | 'parcial' | 'pagado';

  // Vencimiento
  fechaVencimiento: string;       // ISO YYYY-MM-DD
  diasVencido:      number;       // > 0 días en mora

  // Identificación del comprobante (de transactions)
  monto:            number;       // FIRMADO: <0 CxP, >0 CxC
  fecha:            string;
  tipoDocumento:    string;
  serie?:           string;
  correlativo?:     string;
  rucProveedor?:    string;
  rucCliente?:      string;
  nombreCliente?:   string;
}

export interface IInvoicePaymentRepository {
  /**
   * Lista todas las facturas vencidas con saldo pendiente.
   * Combina la vista `invoice_payment_status` (estado de pago + dias_vencido)
   * con un join contra `transactions` para traer monto signed, tipoDocumento,
   * serie/correlativo y datos del cliente/proveedor.
   *
   * @param fechaRef  Fecha de corte ISO. Default: hoy (en la implementación).
   *                  Filtra: fecha_vencimiento < fechaRef AND saldo_pendiente > tolerancia.
   */
  getOverdueInvoices(
    firmId:    string,
    companyId: string,
    fechaRef?: string,
  ): Promise<OverdueInvoiceRow[]>;
}

// ─── Entity ───────────────────────────────────────────────────────────────────

export interface IEntityRepository {
  /** Find a single entity by document number (RUC, DNI, etc.) within a firm+company. */
  getByDoc(firmId: string, companyId: string, numeroDoc: string): Promise<DBEntity | undefined>;

  /**
   * Batch variant of getByDoc — ONE `.in()` query for many document numbers.
   * P-1 (audit 2026-07-06): evita N round-trips en la clasificación del batch de Upload.
   * Returns a Map keyed by numeroDoc; missing docs simply have no entry.
   */
  getByDocs(firmId: string, companyId: string, numeroDocs: string[]): Promise<Map<string, DBEntity>>;

  /**
   * Insert or update an entity keyed on (firm_id, company_id, numero_doc).
   * Handles razonSocialNorm computation and AES-256 encryption of razonSocial internally.
   * Accepts plaintext razonSocial — encryption is applied before writing to Supabase.
   */
  upsert(entity: Omit<DBEntity, 'dbId' | 'razonSocialNorm'>): Promise<void>;

  /** All entities for a firm+company. razonSocial is returned encrypted — decrypt in caller. */
  getAll(firmId: string, companyId: string): Promise<DBEntity[]>;

  /**
   * Loads all entities into a Map<razonSocialNorm, DBEntity> for O(1) batch lookups.
   * Used by PleGeneratorService to resolve RUC from transaction description.
   */
  buildEntityMap(firmId: string, companyId: string): Promise<Map<string, DBEntity>>;

  /** Hard-delete a single entity by its Supabase id. Scoped to firm+company for safety. */
  deleteById(firmId: string, companyId: string, id: number): Promise<void>;
}

// ─── Agent proposals (Fase 2 · Agentes) ──────────────────────────────────────

/** Ya existe una propuesta PENDIENTE para la misma empresa, agente, tipo y entidad. */
export class PropuestaDuplicadaError extends Error {
  constructor() {
    super('Ya hay una propuesta pendiente para este elemento.');
    this.name = 'PropuestaDuplicadaError';
  }
}

/**
 * Repositorio de propuestas de agentes. El firm_id NUNCA es un parámetro: sale de la sesión
 * (auth.uid()), igual que lo exige la RLS. Los cambios de estado pasan solo por las funciones
 * agent_proposal_* de la base de datos (que además escriben la auditoría de forma atómica).
 */
export interface IAgentProposalRepository {
  /** Crea una propuesta en estado 'pendiente'. Lanza PropuestaDuplicadaError si ya hay una pendiente igual. */
  create(nueva: NuevaPropuesta): Promise<AgentProposal>;
  getById(id: string): Promise<AgentProposal | undefined>;
  /** Propuestas de una empresa y período, más recientes primero. */
  list(
    companyId: string,
    periodo: string,
    filtros?: { estado?: EstadoPropuesta; agente?: AgentePropuesta },
  ): Promise<AgentProposal[]>;
  /** Aprobadas con aplicada_en nulo: hay que reintentar el flujo contable. */
  listPendientesDeAplicar(companyId: string): Promise<AgentProposal[]>;
  /** pendiente → aprobada | rechazada | expirada (RPC agent_proposal_transition). */
  transition(id: string, nuevoEstado: Exclude<EstadoPropuesta, 'pendiente'>, valorFinal?: JsonObject): Promise<AgentProposal>;
  /** Marca el resultado de aplicar una aprobada: sin `error` = aplicada; con `error` = reintentable. */
  markApplied(id: string, error?: string): Promise<AgentProposal>;
  /** Expira las pendientes vencidas de la empresa. Devuelve cuántas. */
  expireDue(companyId: string): Promise<number>;
}
